import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCpPortal } from '../../hooks/useCpPortal';
import { OnboardingTracker } from './OnboardingTracker';
import {
  FileText, CheckCircle2, Clock, AlertCircle, Loader2, X, ShieldCheck, ArrowRight,
} from 'lucide-react';

type AgreementType = 'nda' | 'cpa' | 'annex_b' | 'cprsa' | 'annex_c' | 'annex_d';
type AgreementStatus = 'pending' | 'accepted' | 'update_available' | 'not_available';

interface AgreementSummary {
  type: AgreementType;
  currentVersion: string | null;
  currentHash: string | null;
  acceptedVersion: string | null;
  acceptedAt: string | null;
  acceptedByUserId: number | null;
  status: AgreementStatus;
  missingFields?: string[];
  missingSelections?: string[];
}

interface CpaSelections {
  classification: string | null;
  integration_modes: string[];
  platform_services: string[];
}

interface AgreementDoc {
  type: AgreementType;
  version: string;
  hash: string;
  html: string;
  missingFields?: string[];
  missingSelections?: string[];
  cpaSelections?: CpaSelections | null;
}

const TYPE_LABELS: Record<AgreementType, string> = {
  nda: 'Mutual NDA & Non-Circumvention',
  cpa: 'Channel Partner Agreement',
  cprsa: 'Revenue Share Agreement',
  annex_b: 'Annex B — Integration Schedule',
  annex_c: 'Annex C — M-Wallet Replenishment',
  annex_d: 'Annex D — Pass-through & Sender ID',
};
// Display/unlock order. The backend omits annexures not matching the CP's
// commercial model (cprsa for non-revshare, annex_b/d for non-passthrough,
// annex_c for non-mwallet), so only types present in the fetched list are
// rendered. A type that is presented but not yet published shows as
// "Awaiting template" (status not_available) — it is not required until published.
const TYPE_ORDER: AgreementType[] = ['nda', 'cpa', 'cprsa', 'annex_b', 'annex_c', 'annex_d'];
const TYPE_BLURB: Record<AgreementType, string> = {
  nda: 'Protects both parties during commercial and technical discussion. Required before the CPA is unlocked.',
  cpa: 'Master commercial terms: float, KYC, liability, enabled services. Required before KYB.',
  cprsa: 'Revenue share terms: rate, statements, duration and cessation. Presented only to Revenue Share Partners. Required before KYB.',
  annex_b: 'Technical integration schedule: API credentials, endpoints, webhooks. Presented to pass-through (HMAC) partners. Incorporated by reference.',
  annex_c: 'M-Wallet merchant replenishment terms: collections, replenishment, daily sweep. Presented to M-Wallet merchant partners. Awaiting template until CPA 1.6.0 is published.',
  annex_d: 'Pass-through payment rails and Sender identification schedule. Presented to HMAC pass-through partners.',
};

interface ParticularsForm {
  legal_name: string;
  reg_number: string;
  jurisdiction: string;
  registered_address: string;
  signatory_name: string;
  signatory_title: string;
  notice_email: string;
}

const EMPTY_PARTICULARS: ParticularsForm = {
  legal_name: '',
  reg_number: '',
  jurisdiction: '',
  registered_address: '',
  signatory_name: '',
  signatory_title: '',
  notice_email: '',
};

function CompanyParticularsForm({
  apiBase,
  authHeaders,
  onSaved,
  alreadySaved = false,
}: {
  apiBase: string;
  authHeaders: () => Record<string, string>;
  onSaved: () => Promise<void> | void;
  alreadySaved?: boolean;
}) {
  const [form, setForm] = useState<ParticularsForm>(EMPTY_PARTICULARS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${apiBase}/onboarding/status`, { headers: authHeaders() });
        const data = await res.json();
        const prefill = data?.data?.formPrefill;
        if (!cancelled && res.ok && data.success && prefill) {
          setForm({
            legal_name: prefill.legal_name || '',
            reg_number: prefill.reg_number || '',
            jurisdiction: prefill.jurisdiction || '',
            registered_address: prefill.registered_address || '',
            signatory_name: prefill.signatory_name || '',
            signatory_title: prefill.signatory_title || '',
            notice_email: prefill.notice_email || '',
          });
        }
      } catch {
        /* the partner can still type the fields */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [apiBase, authHeaders]);

  const set = (key: keyof ParticularsForm) => (value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${apiBase}/onboarding/particulars`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          legal_name: form.legal_name.trim(),
          reg_number: form.reg_number.trim(),
          jurisdiction: form.jurisdiction.trim(),
          registered_address: form.registered_address.trim(),
          signatory_name: form.signatory_name.trim(),
          signatory_title: form.signatory_title.trim(),
          notice_email: form.notice_email.trim() || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Could not save company details');
      }
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save company details');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="mt-2 flex items-center gap-2 text-xs text-amber-800">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> Loading company details…
      </div>
    );
  }

  const inputCls = 'mt-1 w-full rounded-lg border border-amber-300 bg-white px-2.5 py-1.5 text-sm text-[var(--foreground)]';

  return (
    <form onSubmit={submit} className="mt-2 space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
      <p className="text-xs font-semibold text-amber-900">
        {alreadySaved ? 'Update your company details' : '1. Enter your company details'}
      </p>
      <p className="text-xs text-amber-800">
        {alreadySaved
          ? 'These details are already saved. You can change them, including the country or state of incorporation, before you accept. They are written into the agreement. Do not type in the document.'
          : 'Save these first. They are written into the agreement for you. Do not type in the document.'}
      </p>
      <label className="block text-xs text-amber-900">
        Legal name
        <input className={inputCls} value={form.legal_name} onChange={(e) => set('legal_name')(e.target.value)} required maxLength={255} />
      </label>
      <label className="block text-xs text-amber-900">
        Registration number
        <input className={inputCls} value={form.reg_number} onChange={(e) => set('reg_number')(e.target.value)} required maxLength={50} />
      </label>
      <label className="block text-xs text-amber-900">
        Country or state of incorporation
        <input className={inputCls} value={form.jurisdiction} onChange={(e) => set('jurisdiction')(e.target.value)} required maxLength={120} placeholder="e.g. South Africa or Delaware, United States" />
      </label>
      <label className="block text-xs text-amber-900">
        Registered address
        <textarea className={inputCls} value={form.registered_address} onChange={(e) => set('registered_address')(e.target.value)} required maxLength={500} rows={2} />
      </label>
      <label className="block text-xs text-amber-900">
        Signatory name
        <input className={inputCls} value={form.signatory_name} onChange={(e) => set('signatory_name')(e.target.value)} required maxLength={255} />
      </label>
      <label className="block text-xs text-amber-900">
        Signatory title
        <input className={inputCls} value={form.signatory_title} onChange={(e) => set('signatory_title')(e.target.value)} required maxLength={120} />
      </label>
      <label className="block text-xs text-amber-900">
        Notice email
        <input className={inputCls} type="email" value={form.notice_email} onChange={(e) => set('notice_email')(e.target.value)} maxLength={255} />
      </label>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <button
        type="submit"
        disabled={saving}
        className="inline-flex items-center gap-1 rounded-lg bg-[var(--primary)] px-3 py-1.5 text-xs font-semibold text-[var(--primary-foreground)] disabled:opacity-50"
      >
        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : 'Save details'}
      </button>
    </form>
  );
}

function StatusBadge({ status }: { status: AgreementStatus }) {
  const map: Record<AgreementStatus, { label: string; cls: string; Icon: React.ElementType }> = {
    pending: { label: 'Action needed', cls: 'bg-amber-100 text-amber-800', Icon: Clock },
    accepted: { label: 'Accepted', cls: 'bg-emerald-100 text-emerald-800', Icon: CheckCircle2 },
    update_available: { label: 'New version available', cls: 'bg-blue-100 text-blue-800', Icon: AlertCircle },
    not_available: { label: 'Awaiting template', cls: 'bg-slate-100 text-slate-600', Icon: Clock },
  };
  const { label, cls, Icon } = map[status];
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${cls}`}>
      <Icon className="h-3.5 w-3.5" aria-hidden /> {label}
    </span>
  );
}

export const AgreementCenterOverlay: React.FC = () => {
  const { apiBase, authHeaders, isMirror } = useCpPortal();
  const navigate = useNavigate();
  const [list, setList] = useState<AgreementSummary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewing, setViewing] = useState<AgreementDoc | null>(null);
  const [docLoading, setDocLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [missingFields, setMissingFields] = useState<string[] | null>(null);
  const [missingSelections, setMissingSelections] = useState<string[]>([]);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${apiBase}/agreements`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load agreements');
      setList(data.agreements);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load agreements');
    } finally {
      setLoading(false);
    }
  }, [apiBase, authHeaders]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  // Only types returned by the backend are presented (cprsa is omitted for
  // non-Revenue-Share Partners).
  const presentedTypes: AgreementType[] = useMemo(
    () => TYPE_ORDER.filter((t) => (list ?? []).some((a) => a.type === t)),
    [list],
  );

  const isUnlocked = useCallback(
    (type: AgreementType): boolean => {
      const idx = presentedTypes.indexOf(type);
      if (idx <= 0) return true;
      const prev = presentedTypes[idx - 1];
      const prevAcc = list?.find((a) => a.type === prev);
      return prevAcc?.status === 'accepted' || prevAcc?.status === 'update_available';
    },
    [list, presentedTypes],
  );

  const openDoc = useCallback(
    async (type: AgreementType) => {
      setSubmitError(null);
      setAgreed(false);
      setMissingFields(null);
      setMissingSelections([]);
      setDocLoading(true);
      try {
        const res = await fetch(`${apiBase}/agreements/${type}`, { headers: authHeaders() });
        const data = await res.json();
        if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load document');
        setViewing(data.agreement);
        if (Array.isArray(data.agreement?.missingFields) && data.agreement.missingFields.length > 0) {
          setMissingFields(data.agreement.missingFields);
        }
        setMissingSelections(Array.isArray(data.agreement?.missingSelections) ? data.agreement.missingSelections : []);
      } catch (e) {
        setSubmitError(e instanceof Error ? e.message : 'Failed to load document');
      } finally {
        setDocLoading(false);
      }
    },
    [apiBase, authHeaders],
  );

  const accept = useCallback(async () => {
    if (!viewing) return;
    setAccepting(true);
    setSubmitError(null);
    try {
      const res = await fetch(`${apiBase}/agreements/${viewing.type}/accept`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ docVersion: viewing.version, docHash: viewing.hash }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        if (data.code === 'MISSING_FIELDS' && Array.isArray(data.missingFields)) {
          setMissingFields(data.missingFields);
        }
        if (data.code === 'SELECTIONS_REQUIRED' && Array.isArray(data.missingSelections)) {
          setMissingSelections(data.missingSelections);
        }
        throw new Error(data.error || 'Acceptance failed');
      }
      setViewing(null);
      await loadList();
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'Acceptance failed');
    } finally {
      setAccepting(false);
    }
  }, [apiBase, authHeaders, viewing, loadList]);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--muted-foreground)]" aria-hidden />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <OnboardingTracker />
      <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-4">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-[var(--primary)]/10 p-2">
            <ShieldCheck className="h-5 w-5 text-[var(--primary)]" aria-hidden />
          </div>
          <div>
            <h2 className="text-base font-semibold text-[var(--foreground)]">Agreements</h2>
            <p className="mt-1 text-sm text-[var(--muted-foreground)]">
              You can read and download every published agreement now. Accept them in order. The next acceptance opens only after you accept the one before it.
            </p>
            <ol className="mt-2 list-decimal space-y-1 pl-4 text-sm text-[var(--foreground)]">
              <li>On the first agreement that still needs acceptance, enter or update your company details, including the country or state of incorporation, and press Save details.</li>
              <li>You can open and download every published agreement. Do not type in a document. MyMoolah has already chosen the partner type and services.</li>
              <li>Accept them in order. Read it, tick that you agree, and press Accept. That is your signature.</li>
              <li>Repeat until each agreement is accepted.</li>
              <li>When they are all accepted, continue to your company form and documents.</li>
            </ol>
          </div>
        </div>
      </div>

      {(() => {
        const acceptedOrUpdated = (t: AgreementType) => {
          const a = list?.find((x) => x.type === t);
          return a?.status === 'accepted' || a?.status === 'update_available';
        };
        // KYB unlock mirrors the backend tracker: every PUBLISHED agreement
        // presented for this CP's commercial model must be accepted. Types that
        // are presented but `not_available` (Annex C/D before their templates
        // are published) are NOT required — they cannot be accepted yet, so
        // they do not block KYB. This matches partnerAgreementService.requiredAgreementTypes.
        const requiredTypes = presentedTypes.filter(
          (t) => (list?.find((x) => x.type === t)?.status) !== 'not_available',
        );
        const ready = requiredTypes.every((t) => acceptedOrUpdated(t));
        if (!ready || isMirror) return null;
        return (
          <button
            type="button"
            onClick={() => navigate('/client/onboarding')}
            className="flex w-full items-center justify-between rounded-xl border border-[var(--primary)]/30 bg-[var(--primary)]/5 px-4 py-3 text-left transition-colors hover:bg-[var(--primary)]/10"
          >
            <span className="text-sm font-medium text-[var(--foreground)]">
              Agreements done. Next: company details and documents
            </span>
            <ArrowRight className="h-4 w-4 text-[var(--primary)]" aria-hidden />
          </button>
        );
      })()}

      {(() => {
        const particularsType = isMirror
          ? null
          : presentedTypes.find((type) => {
              const item = list?.find((a) => a.type === type);
              const status = item?.status ?? 'not_available';
              const stillOpen = status === 'pending' || status === 'update_available';
              return stillOpen && isUnlocked(type);
            }) ?? null;
        return presentedTypes.map((type) => {
        const item = list?.find((a) => a.type === type);
        const status = item?.status ?? 'not_available';
        const unlocked = isUnlocked(type);
        const nda = list?.find((a) => a.type === 'nda');
        const ndaOpen = nda?.status !== 'accepted' && nda?.status !== 'update_available';
        const canAct = unlocked && status !== 'not_available' && !isMirror;
        const hasMissing = !!(item?.missingFields && item.missingFields.length > 0);
        const showParticulars = type === particularsType;
        return (
          <div
            key={type}
            className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-4"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-[var(--muted)] p-2">
                  <FileText className="h-5 w-5 text-[var(--muted-foreground)]" aria-hidden />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-[var(--foreground)]">
                      {TYPE_LABELS[type]}
                    </h3>
                    <StatusBadge status={status} />
                  </div>
                  <p className="mt-1 max-w-xl text-xs text-[var(--muted-foreground)]">
                    {TYPE_BLURB[type]}
                  </p>
                  <p className="mt-1 text-xs text-[var(--muted-foreground)]">
                    {item?.acceptedVersion
                      ? `Accepted v${item.acceptedVersion} on ${new Date(item.acceptedAt!).toLocaleDateString()}`
                      : item?.currentVersion
                        ? `Current version: v${item.currentVersion}`
                        : 'Template not yet published.'}
                  </p>
                  {showParticulars && (
                    <CompanyParticularsForm
                      apiBase={apiBase}
                      authHeaders={authHeaders}
                      onSaved={loadList}
                      alreadySaved={!hasMissing}
                    />
                  )}
                </div>
              </div>
              <div className="flex-shrink-0">
                {status === 'not_available' ? (
                  <span
                    className="inline-flex items-center gap-1 rounded-lg border border-[var(--border)] px-3 py-2 text-xs text-[var(--muted-foreground)]"
                    title="MyMoolah will publish this agreement template and notify you when it is ready. No action is required from you yet."
                  >
                    <Clock className="h-3.5 w-3.5" aria-hidden /> Awaiting template
                  </span>
                ) : !unlocked ? (
                  <div className="flex flex-col items-end gap-1">
                    <button
                      type="button"
                      onClick={() => openDoc(type)}
                      className="rounded-lg border border-[var(--border)] px-3 py-2 text-xs font-medium text-[var(--foreground)] hover:bg-[var(--muted)]"
                    >
                      Read
                    </button>
                    <span className="max-w-[11rem] text-right text-[10px] leading-snug text-[var(--muted-foreground)]">
                      {ndaOpen && (type === 'cpa' || type === 'annex_b')
                        ? 'Accept opens after you accept the Mutual NDA.'
                        : 'Accept opens after you accept the agreement above.'}
                    </span>
                  </div>
                ) : status === 'accepted' ? (
                  <button
                    type="button"
                    onClick={() => openDoc(type)}
                    className="rounded-lg border border-[var(--border)] px-3 py-2 text-xs font-medium text-[var(--foreground)] hover:bg-[var(--muted)]"
                  >
                    Review
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={!canAct}
                    onClick={() => openDoc(type)}
                    className="rounded-lg bg-[var(--primary)] px-3 py-2 text-xs font-semibold text-[var(--primary-foreground)] transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {status === 'update_available' ? 'Review & update' : 'Review & accept'}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      });
      })()}

      {viewing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="flex h-[85vh] w-full max-w-4xl flex-col rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-[var(--border)] px-5 py-3">
              <div>
                <h3 className="text-sm font-semibold text-[var(--foreground)]">
                  {TYPE_LABELS[viewing.type]}
                </h3>
                <p className="text-xs text-[var(--muted-foreground)]">Version {viewing.version}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const blob = new Blob([viewing.html], { type: 'text/html;charset=utf-8' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${viewing.type}-v${viewing.version}.html`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="rounded-lg border border-[var(--border)] px-3 py-1.5 text-xs font-medium text-[var(--foreground)] hover:bg-[var(--muted)]"
                >
                  Download
                </button>
                <button
                  type="button"
                  onClick={() => setViewing(null)}
                  className="rounded-lg p-1.5 text-[var(--muted-foreground)] hover:bg-[var(--muted)]"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" aria-hidden />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden">
              {docLoading ? (
                <div className="flex h-full items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-[var(--muted-foreground)]" aria-hidden />
                </div>
              ) : (
                <iframe
                  title={TYPE_LABELS[viewing.type]}
                  srcDoc={viewing.html}
                  className="h-full w-full border-0"
                />
              )}
            </div>
            <div className="border-t border-[var(--border)] px-5 py-3">
              {submitError && (
                <p className="mb-2 text-xs text-red-600">{submitError}</p>
              )}
              {(() => {
                const viewingItem = list?.find((a) => a.type === viewing.type);
                const viewingStatus = viewingItem?.status ?? 'not_available';
                const viewingOpen = viewingStatus === 'pending' || viewingStatus === 'update_available';
                if (isMirror || !viewingOpen || !isUnlocked(viewing.type)) return null;
                return (
                  <CompanyParticularsForm
                    apiBase={apiBase}
                    authHeaders={authHeaders}
                    alreadySaved={!(missingFields && missingFields.length > 0)}
                    onSaved={async () => {
                      const type = viewing.type;
                      await loadList();
                      await openDoc(type);
                    }}
                  />
                );
              })()}
              {viewing.type === 'cpa' && missingSelections.length > 0 && !isMirror && (
                <p className="mb-2 text-xs text-amber-800">
                  MyMoolah sets the partner type and services in this agreement. You do not choose them. Accept opens once that setup is saved.
                </p>
              )}
              {isMirror ? (
                <p className="text-xs text-[var(--muted-foreground)]">
                  Read-only admin mirror — acceptances are made by the Channel Partner.
                </p>
              ) : !isUnlocked(viewing.type) ? (
                <p className="text-xs text-[var(--muted-foreground)]">
                  You can read and download this agreement now. Accept opens after you accept the agreement above.
                </p>
              ) : (
                <div className="flex items-center justify-between gap-3">
                  <label className="flex items-center gap-2 text-sm text-[var(--foreground)]">
                    <input
                      type="checkbox"
                      checked={agreed}
                      onChange={(e) => setAgreed(e.target.checked)}
                      className="h-5 w-5 shrink-0 rounded border border-slate-500 accent-[#86BE41]"
                    />
                    I have read and agree to be bound by this agreement.
                  </label>
                  <button
                    type="button"
                    disabled={!agreed || accepting || missingSelections.length > 0 || !!(missingFields && missingFields.length > 0)}
                    onClick={accept}
                    className="rounded-lg bg-[var(--primary)] px-4 py-2 text-sm font-semibold text-[var(--primary-foreground)] transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {accepting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : 'Accept agreement'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
